/**
 * WaitlistPage
 * ------------
 * Admin overview page for everyone currently queued for a rage room.
 * Surfaces location context plus customer contact details for quick outreach.
 * Now includes per-city summaries and filters so operators can quickly pivot between regions,
 * and supports selecting multiple guests to trigger the booking-available email in bulk.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApiBase } from 'm34n-ui';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LinearProgress from '@mui/material/LinearProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';

import useApi from '../hooks/useApi';

/**
 * Normalizes the listWaitlist API payload into a predictable array shape.
 *
 * @param {any} response - Raw API response.
 * @returns {Array<Object>} Simplified waitlist entries.
 */
const unwrapWaitlistEntries = (response) => {
	if (!response) {
		return [];
	}
	if (Array.isArray(response)) {
		return response;
	}
	if (Array.isArray(response.rows)) {
		return response.rows;
	}
	if (Array.isArray(response.data?.rows)) {
		return response.data.rows;
	}
	if (Array.isArray(response.items)) {
		return response.items;
	}
	if (Array.isArray(response.waitlistEntries)) {
		return response.waitlistEntries;
	}
	if (Array.isArray(response.data)) {
		return response.data;
	}
	return [];
};

/**
 * Normalizes the city summary API payload into a predictable array shape.
 *
 * @param {any} response - Raw API response.
 * @returns {Array<Object>} Simplified city summaries.
 */
const unwrapCitySummaries = (response) => {
	if (!response) {
		return [];
	}
	if (Array.isArray(response)) {
		return response;
	}
	if (Array.isArray(response.citySummaries)) {
		return response.citySummaries;
	}
	if (Array.isArray(response.data?.citySummaries)) {
		return response.data.citySummaries;
	}
	return [];
};

/**
 * Normalizes the email template API payload into a predictable array shape.
 *
 * @param {any} response - Raw API response.
 * @returns {Array<Object>} Simplified email template entries.
 */
const unwrapEmailTemplates = (response) => {
	if (!response) {
		return [];
	}
	if (Array.isArray(response)) {
		return response;
	}
	if (Array.isArray(response.rows)) {
		return response.rows;
	}
	if (Array.isArray(response.data?.rows)) {
		return response.data.rows;
	}
	if (Array.isArray(response.items)) {
		return response.items;
	}
	if (Array.isArray(response.emailTemplates)) {
		return response.emailTemplates;
	}
	if (Array.isArray(response.data)) {
		return response.data;
	}
	return [];
};

/**
 * Safely converts any string-like value into a lowercase token for comparisons.
 *
 * @param {string} value - Arbitrary string input.
 * @returns {string} Normalized string suitable for sorting.
 */
const safeString = (value) => (value || '').toString().trim().toLowerCase();

/**
 * Builds the most complete customer name from the supplied fields.
 *
 * @param {Object} [customer] - Customer object from the waitlist entry.
 * @param {string} [fallbackValue] - Fallback when name fields are empty.
 * @returns {string} Derived customer name.
 */
const deriveCustomerName = (customer = {}, fallbackValue = 'Unknown Guest') => {
	const composed = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
	return customer.name || customer.fullName || composed || fallbackValue || 'Unknown Guest';
};

/**
 * Formats a phone number for display. Returns the original input when the
 * digits do not match a standard US length.
 *
 * @param {string|number} value - Raw phone number.
 * @returns {string} Formatted phone number or em dash.
 */
const formatPhoneNumber = (value) => {
	if (!value) {
		return '—';
	}
	const raw = value.toString();
	const digits = raw.replace(/\D+/g, '');
	if (digits.length === 10) {
		return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
	}
	if (digits.length === 11 && digits.startsWith('1')) {
		return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	}
	return raw;
};

const WaitlistPage = () => {
	const { rageRoom } = useApi();
	const { emailTemplates: emailTemplatesApi } = useApiBase();
	const locationId = process.env.REACT_APP_DEFAULT_LOCATION_ID || '';

	const [waitlistEntries, setWaitlistEntries] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [refreshSignal, setRefreshSignal] = useState(0);

	const [selectedCity, setSelectedCity] = useState('');
	const [citySummary, setCitySummary] = useState([]);
	const [summaryLoading, setSummaryLoading] = useState(true);
	const [summaryError, setSummaryError] = useState(null);

	const [selectedEntries, setSelectedEntries] = useState(() => new Set());
	const [sendingEmails, setSendingEmails] = useState(false);
	const [bulkSendResult, setBulkSendResult] = useState(null);

	const [emailTemplates, setEmailTemplates] = useState([]);
	const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(true);
	const [emailTemplatesError, setEmailTemplatesError] = useState(null);
	const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState('');

	useEffect(() => {
		let isMounted = true;

		const fetchCitySummary = async () => {
			if (!rageRoom?.public?.listWaitlistSummary) {
				if (isMounted) {
					setSummaryLoading(false);
					setCitySummary([]);
					setSummaryError('Waitlist summary endpoint is unavailable.');
				}
				return;
			}

			if (isMounted) {
				setSummaryLoading(true);
				setSummaryError(null);
			}

			try {
				const query = {
					status: 'waiting',
					...(locationId ? { locationId } : {}),
				};
				const response = await rageRoom.public.listWaitlistSummary(query);
				if (!isMounted) {
					return;
				}
				setCitySummary(unwrapCitySummaries(response));
			} catch (fetchError) {
				if (isMounted) {
					setSummaryError(fetchError.message || 'Unable to load waitlist summary.');
					setCitySummary([]);
				}
			} finally {
				if (isMounted) {
					setSummaryLoading(false);
				}
			}
		};

		fetchCitySummary();

		return () => {
			isMounted = false;
		};
	}, [locationId, refreshSignal]);

	useEffect(() => {
		let isMounted = true;

		const fetchWaitlist = async () => {
			if (!rageRoom?.public?.listWaitlist) {
				if (isMounted) {
					setLoading(false);
					setWaitlistEntries([]);
					setError('Waitlist endpoint is unavailable.');
				}
				return;
			}

			if (isMounted) {
				setLoading(true);
				setError(null);
			}

			try {
				const query = {
					status: 'waiting',
					...(locationId ? { locationId } : {}),
					...(selectedCity ? { city: selectedCity } : {}),
                    pageSize: 1000,
				};
				const response = await rageRoom.public.listWaitlist(query);
				if (!isMounted) {
					return;
				}
				setWaitlistEntries(unwrapWaitlistEntries(response));
			} catch (fetchError) {
				if (isMounted) {
					setError(fetchError.message || 'Unable to load waitlist attendees.');
					setWaitlistEntries([]);
				}
			} finally {
				if (isMounted) {
					setLoading(false);
				}
			}
		};

		fetchWaitlist();

		return () => {
			isMounted = false;
		};
	}, [locationId, selectedCity, refreshSignal]);

	useEffect(() => {
		let isMounted = true;

		const fetchEmailTemplates = async () => {
			if (!emailTemplatesApi?.search) {
				if (isMounted) {
					setEmailTemplatesLoading(false);
					setEmailTemplates([]);
					setEmailTemplatesError('Email template endpoint is unavailable.');
				}
				return;
			}

			if (isMounted) {
				setEmailTemplatesLoading(true);
				setEmailTemplatesError(null);
			}

			try {
				const response = await emailTemplatesApi.search({
					queryParams: {
						pageOffset: 0,
						pageSize: 50,
					},
				});
				if (!isMounted) {
					return;
				}
				setEmailTemplates(unwrapEmailTemplates(response));
			} catch (fetchError) {
				if (isMounted) {
					setEmailTemplates([]);
					setEmailTemplatesError(fetchError.message || 'Unable to load email templates.');
				}
			} finally {
				if (isMounted) {
					setEmailTemplatesLoading(false);
				}
			}
		};

		fetchEmailTemplates();

		return () => {
			isMounted = false;
		};
		// Run once on mount to avoid repeated fetches caused by non-memoized API clients.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const normalizedEntries = useMemo(() => {
		if (!waitlistEntries.length) {
			return [];
		}

		return waitlistEntries
			.map((entry, index) => {
				const location = entry.location || {};
				const metadata = entry.metadata || {};

				const city = entry.city || location.city || metadata.city || 'Unknown City';
				const locationName =
					entry.locationName ||
					location.name ||
					location.label ||
					location.title ||
					location.displayName ||
					entry.venueName ||
					'Unassigned Location';

				const customerName = deriveCustomerName(entry.customer, entry.name);
				const customerEmail = entry.customer?.email || entry.email || '—';
				const customerPhone = formatPhoneNumber(
					entry.customer?.phoneNumber ||
						entry.customer?.phone ||
						entry.phoneNumber ||
						entry.phone
				);

				const partySizeValue = Number(
					entry.partySize ?? entry.groupSize ?? entry.size ?? metadata.partySize
				);
				const partySize = Number.isFinite(partySizeValue) && partySizeValue > 0 ? partySizeValue : '—';

				return {
					id: entry.id || entry.waitlistId || entry._id || `${index}-${customerName}`,
					city,
					locationName,
					customerName,
					customerEmail,
					customerPhone,
					partySize
				};
			})
			.sort((a, b) => {
				const cityDiff = safeString(a.city).localeCompare(safeString(b.city));
				if (cityDiff !== 0) {
					return cityDiff;
				}
				return safeString(a.locationName).localeCompare(safeString(b.locationName));
			});
	}, [waitlistEntries]);

	const templateOptions = useMemo(() => {
		if (!emailTemplates.length) {
			return [];
		}

		return emailTemplates.map((template, index) => {
			const templateName =
				template.name ||
				template.templateName ||
				template.displayName ||
				template.subject ||
				`template_${index + 1}`;
			const label =
				template.name ||
				template.displayName ||
				template.subject ||
				template.templateName ||
				`Template ${index + 1}`;
			const id = template._id || template.id || template.templateId || `${index}-${label}`;
			return { id, label, templateName };
		});
	}, [emailTemplates]);

	const selectedTemplate = useMemo(() => {
		if (!selectedEmailTemplateId) {
			return null;
		}
		return templateOptions.find((template) => template.id === selectedEmailTemplateId) || null;
	}, [templateOptions, selectedEmailTemplateId]);

	useEffect(() => {
		setSelectedEntries((prev) => {
			if (!prev.size) {
				return prev;
			}
			const validIds = new Set(normalizedEntries.map((entry) => entry.id));
			let changed = false;
			const next = new Set();
			prev.forEach((id) => {
				if (validIds.has(id)) {
					next.add(id);
				} else {
					changed = true;
				}
			});
			return changed ? next : prev;
		});
	}, [normalizedEntries]);

	useEffect(() => {
		if (!selectedEmailTemplateId) {
			return;
		}
		const exists = templateOptions.some((template) => template.id === selectedEmailTemplateId);
		if (!exists) {
			setSelectedEmailTemplateId('');
		}
	}, [templateOptions, selectedEmailTemplateId]);

	const cityOptions = useMemo(() => {
		const uniqueCities = new Set();
		citySummary.forEach((summary) => {
			uniqueCities.add(summary.city || 'Unassigned City');
		});
		return Array.from(uniqueCities).sort((a, b) => a.localeCompare(b));
	}, [citySummary]);

	useEffect(() => {
		if (selectedCity && !cityOptions.includes(selectedCity)) {
			setSelectedCity('');
		}
	}, [cityOptions, selectedCity]);

	const cityTiles = useMemo(() => {
		const totalEntries = citySummary.reduce((acc, summary) => acc + (summary.totalEntries || 0), 0);
		const totalPartySize = citySummary.reduce((acc, summary) => acc + (summary.totalPartySize || 0), 0);
		const tiles = [
			{
				cityKey: '',
				label: 'All Cities',
				totalEntries,
				totalPartySize
			}
		];
		citySummary.forEach((summary) => {
			const label = summary.city || 'Unassigned City';
			tiles.push({
				cityKey: label,
				label,
				totalEntries: summary.totalEntries || 0,
				totalPartySize: summary.totalPartySize || 0
			});
		});
		return tiles;
	}, [citySummary]);

	const templateSelectHelperText = useMemo(() => {
		if (emailTemplatesError) {
			return emailTemplatesError;
		}
		if (emailTemplatesLoading) {
			return 'Loading templates...';
		}
		if (!templateOptions.length) {
			return 'No email templates available.';
		}
		return 'Select an email template to enable email sends.';
	}, [emailTemplatesError, emailTemplatesLoading, templateOptions.length]);

	const handleRefresh = () => {
		setRefreshSignal((prev) => prev + 1);
	};

	const handleCityChange = (event) => {
		setSelectedCity(event.target.value);
	};

	const handleTileSelect = (cityKey) => {
		setSelectedCity(cityKey);
	};

	const handleTemplateChange = (event) => {
		setSelectedEmailTemplateId(event.target.value);
	};

	const toggleEntrySelection = (entryId) => {
		setSelectedEntries((prev) => {
			const next = new Set(prev);
			if (next.has(entryId)) {
				next.delete(entryId);
			} else {
				next.add(entryId);
			}
			return next;
		});
	};

	const handleSelectAll = (event) => {
		const { checked } = event.target;
		if (checked) {
			setSelectedEntries(new Set(normalizedEntries.map((entry) => entry.id)));
		} else {
			setSelectedEntries(new Set());
		}
	};

	const handleSendAvailabilityEmails = async () => {
		if (!selectedEntries.size || sendingEmails) {
			return;
		}
		if (!selectedTemplate) {
			setBulkSendResult({
				severity: 'error',
				summary: 'Select an email template before sending availability emails.',
				errors: [],
			});
			return;
		}
		if (!rageRoom?.admin?.sendWaitlistSlotAvailableEmail) {
			setBulkSendResult({
				severity: 'error',
				summary: 'Waitlist notification endpoint is unavailable. Please contact an administrator.',
				errors: [],
			});
			return;
		}

		setSendingEmails(true);
		setBulkSendResult(null);
		const waitlistIds = Array.from(selectedEntries);
		const errors = [];

		for (const entryId of waitlistIds) {
			try {
				await rageRoom.admin.sendWaitlistSlotAvailableEmail({
					waitlistId: entryId,
					templateName: selectedTemplate.templateName,
				});
			} catch (sendError) {
				const relatedEntry = normalizedEntries.find((entry) => entry.id === entryId);
				errors.push({
					id: entryId,
					customerName: relatedEntry?.customerName || 'Unknown guest',
					message: sendError?.message || 'Unable to send booking availability email.',
				});
			}
		}

		setSendingEmails(false);
		setSelectedEntries((prev) => {
			if (!errors.length) {
				return new Set();
			}
			const failedIds = new Set(errors.map((error) => error.id));
			const next = new Set();
			prev.forEach((id) => {
				if (failedIds.has(id)) {
					next.add(id);
				}
			});
			return next;
		});
		setBulkSendResult({
			severity: errors.length ? 'warning' : 'success',
			summary: errors.length
				? `Sent ${waitlistIds.length - errors.length} email(s); ${errors.length} failed.`
				: `Successfully sent ${waitlistIds.length} booking availability email${waitlistIds.length === 1 ? '' : 's'}.`,
			errors,
		});
	};

	const hasEntries = normalizedEntries.length > 0;
	const selectedCount = selectedEntries.size;
	const allEntriesSelected = hasEntries && selectedCount === normalizedEntries.length;
	const isSelectIndeterminate = selectedCount > 0 && selectedCount < normalizedEntries.length;
	const sendButtonLabel = sendingEmails
		? 'Sending emails...'
		: !selectedTemplate
			? 'Select template to send'
			: selectedCount > 1
				? `Send ${selectedCount} availability emails`
				: 'Send availability email';
	const canSendEmails = selectedCount > 0 && !sendingEmails && Boolean(selectedTemplate);
	const disableTemplateSelect = emailTemplatesLoading || !templateOptions.length;

	return (
		<Stack spacing={4}>
			<Stack
				direction={{ xs: 'column', md: 'row' }}
				spacing={3}
				justifyContent="space-between"
				alignItems={{ xs: 'flex-start', md: 'center' }}
			>
				<Box>
					<Typography variant="h3" gutterBottom>
						Waitlist
					</Typography>
					<Typography color="text.secondary">
						Review every guest waiting for a rage room slot, grouped by city and location.
					</Typography>
				</Box>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={1.5}
					alignItems={{ xs: 'stretch', sm: 'center' }}
					justifyContent="flex-end"
					sx={{ width: { xs: '100%', sm: 'auto' } }}
				>
					<Button
						variant="outlined"
						color="primary"
						onClick={handleRefresh}
						disabled={loading || summaryLoading}
					>
						Refresh
					</Button>
					
				</Stack>
			</Stack>

			<Stack
				direction={{ xs: 'column', md: 'row' }}
				spacing={2}
				alignItems={{ xs: 'stretch', md: 'flex-end' }}
			>
				<FormControl size="small" sx={{ minWidth: 200 }}>
					<InputLabel id="city-select-label">Filter by city</InputLabel>
					<Select
						labelId="city-select-label"
						value={selectedCity}
						label="Filter by city"
						onChange={handleCityChange}
					>
						<MenuItem value="">
							<em>All Cities</em>
						</MenuItem>
						{cityOptions.map((city) => (
							<MenuItem key={city} value={city}>
								{city}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Stack>
            <Stack
                                         direction={{ xs: 'column', md: 'row' }}
				spacing={2}
				alignItems={{ xs: 'stretch', md: 'flex-end' }}
                                         >
                                         <FormControl
						size="small"
						sx={{ minWidth: 260 }}
						error={Boolean(emailTemplatesError)}
						disabled={disableTemplateSelect}
						required
					>
						<InputLabel id="email-template-select-label">Email template</InputLabel>
						<Select
							labelId="email-template-select-label"
							value={selectedEmailTemplateId}
							label="Email template"
							onChange={handleTemplateChange}
						>
							<MenuItem value="">
								<em>Select template</em>
							</MenuItem>
							{templateOptions.map((template) => (
								<MenuItem key={template.id} value={template.id}>
									{template.label}
								</MenuItem>
							))}
						</Select>
						<FormHelperText>{templateSelectHelperText}</FormHelperText>
					</FormControl>
                    <FormControl
						size="small"
						sx={{ minWidth: 260 }}
						error={Boolean(emailTemplatesError)}
						disabled={disableTemplateSelect}
						required
					>
    					<Button
    						variant="contained"
    						color="secondary"
    						onClick={handleSendAvailabilityEmails}
    						disabled={!canSendEmails}
    					>
    						{sendButtonLabel}
    					</Button>
                    	<FormHelperText>Send email based on template selection to every selected user.</FormHelperText>
                    </FormControl>
					{selectedCount > 0 && (
						<Typography variant="caption" color="text.secondary">
							{selectedCount} selected
						</Typography>
					)}
                                         </Stack>

			{bulkSendResult && (
				<Alert severity={bulkSendResult.severity} onClose={() => setBulkSendResult(null)}>
					<Typography variant="body2">{bulkSendResult.summary}</Typography>
					{bulkSendResult.errors.length > 0 && (
						<Box component="ul" sx={{ pl: 2, mb: 0 }}>
							{bulkSendResult.errors.map((error) => (
								<li key={error.id}>
									<Typography variant="caption">
										{error.customerName}: {error.message}
									</Typography>
								</li>
							))}
						</Box>
					)}
				</Alert>
			)}

			{summaryError && (
				<Typography color="error" variant="body2">
					{summaryError}
				</Typography>
			)}

			<Card sx={{ p: 2 }}>
				{summaryLoading && <LinearProgress color="secondary" />}
				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: { xs: 'repeat(auto-fit, minmax(180px, 1fr))' },
						gap: 2,
						mt: summaryLoading ? 2 : 0
					}}
				>
					{cityTiles.map((tile) => {
						const isSelected = selectedCity === tile.cityKey;
						const cardKey = tile.cityKey || 'all-cities';
						return (
							<Card
								key={cardKey}
								variant="outlined"
								sx={{
									borderColor: isSelected ? 'primary.main' : 'divider',
									backgroundColor: isSelected ? 'action.selected' : 'background.paper'
								}}
							>
								<CardActionArea onClick={() => handleTileSelect(tile.cityKey)}>
									<CardContent>
										<Typography variant="subtitle2">{tile.label}</Typography>
										<Typography variant="h4" sx={{ my: 0.5 }}>
											{tile.totalEntries}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{tile.totalPartySize} waiting guests
										</Typography>
									</CardContent>
								</CardActionArea>
							</Card>
						);
					})}
				</Box>
			</Card>

			<Card sx={{ overflowX: 'auto' }}>
				{loading && <LinearProgress color="secondary" />}
				<CardContent sx={{ p: 0 }}>
					{error && (
						<Box sx={{ px: 3, pt: 3 }}>
							<Typography color="error" variant="body2">
								{error}
							</Typography>
						</Box>
					)}

					<TableContainer component={Box} sx={{ px: 3, pb: 3 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell padding="checkbox">
										<Checkbox
											checked={allEntriesSelected}
											indeterminate={isSelectIndeterminate}
											onChange={handleSelectAll}
											inputProps={{ 'aria-label': 'select all waitlist entries' }}
										/>
									</TableCell>
									<TableCell>City</TableCell>
									<TableCell>Location Name</TableCell>
									<TableCell>Customer</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Phone Number</TableCell>
									<TableCell align="right">Party Size</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{hasEntries ? (
									normalizedEntries.map((entry) => (
										<TableRow key={entry.id} hover selected={selectedEntries.has(entry.id)}>
											<TableCell padding="checkbox">
												<Checkbox
													checked={selectedEntries.has(entry.id)}
													onChange={() => toggleEntrySelection(entry.id)}
													inputProps={{ 'aria-label': `select waitlist entry for ${entry.customerName}` }}
												/>
											</TableCell>
											<TableCell>{entry.city}</TableCell>
											<TableCell>{entry.locationName}</TableCell>
											<TableCell>{entry.customerName}</TableCell>
											<TableCell>{entry.customerEmail}</TableCell>
											<TableCell>{entry.customerPhone}</TableCell>
											<TableCell align="right">{entry.partySize}</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={7} align="center" sx={{ py: 4 }}>
											<Typography color="text.secondary">
												{loading
													? 'Fetching waitlist attendees...'
													: 'No waitlist attendees found for the selected filters.'}
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</CardContent>
			</Card>
		</Stack>
	);
};

export default WaitlistPage;